import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import {
  searchTradera,
  effectivePrice,
  loadTradera,
  saveTradera,
  TraderaItem,
  TraderaStore,
  Watch,
} from "../FIREBASE/tradera";
import "../CSS/tradera.scss";

/** Keep seen-ids from growing forever: drop entries older than this. */
const SEEN_TTL_MS = 45 * 24 * 60 * 60 * 1000;

interface FlaggedItem extends TraderaItem {
  isNew: boolean;
}

interface WatchResult {
  loading: boolean;
  error: string | null;
  items: FlaggedItem[];
  newCount: number;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function fmtSek(n: number | null): string {
  return n == null ? "—" : `${n.toLocaleString("sv-SE")} kr`;
}

function fmtEnd(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (d.getTime() < Date.now()) return "Avslutad";
  const date = d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
  return `Slutar ${date} ${time}`;
}

/** Does an item pass this watch's price + exclude-word filters? */
function matches(w: Watch, it: TraderaItem): boolean {
  const price = effectivePrice(it);
  if (w.minPrice != null && (price == null || price < w.minPrice)) return false;
  if (w.maxPrice != null && (price == null || price > w.maxPrice)) return false;
  if (w.excludeWords.length) {
    const hay = it.shortDescription.toLowerCase();
    if (w.excludeWords.some((word) => word && hay.includes(word.toLowerCase()))) return false;
  }
  return true;
}

function filterSummary(w: Watch): string {
  const parts: string[] = [];
  if (w.minPrice != null || w.maxPrice != null) {
    parts.push(`${w.minPrice != null ? fmtSek(w.minPrice) : "0 kr"}–${w.maxPrice != null ? fmtSek(w.maxPrice) : "∞"}`);
  }
  if (w.categoryId) parts.push(`kat. ${w.categoryId}`);
  if (w.excludeWords.length) parts.push(`utom: ${w.excludeWords.join(", ")}`);
  return parts.join(" · ");
}

export function TraderaView({ uid: userId }: { uid: string }) {
  usePageBackground();

  const [store, setStore] = useState<TraderaStore>({ watches: [], seen: {} });
  const [results, setResults] = useState<Record<string, WatchResult>>({});
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const storeRef = useRef<TraderaStore>(store);
  storeRef.current = store;
  const ranOnce = useRef(false);

  // Add-watch form fields
  const [query, setQuery] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [exclude, setExclude] = useState("");
  const [category, setCategory] = useState("");

  // Inline edit of an existing watch
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eQuery, setEQuery] = useState("");
  const [eMin, setEMin] = useState("");
  const [eMax, setEMax] = useState("");
  const [eExclude, setEExclude] = useState("");
  const [eCategory, setECategory] = useState("");

  /** Run every watch, flag newly-appeared listings, then record all seen ids. */
  const runAll = useCallback(async () => {
    const cur = storeRef.current;
    if (!cur.watches.length) return;
    setRefreshing(true);
    setResults((prev) => {
      const next = { ...prev };
      for (const w of cur.watches) next[w.id] = { ...(next[w.id] ?? { items: [], newCount: 0, error: null }), loading: true };
      return next;
    });

    const seen = cur.seen;
    const nextResults: Record<string, WatchResult> = {};
    const seenUpdates: string[] = []; // every id actually fetched (matched or not)
    const baselinedOk: Record<string, boolean> = {};

    for (const w of cur.watches) {
      try {
        const resp = await searchTradera(w.query, w.categoryId, 0);
        for (const it of resp.items) if (Number.isFinite(it.id)) seenUpdates.push(String(it.id));
        const flagged: FlaggedItem[] = resp.items
          .filter((it) => matches(w, it))
          .map((it) => ({ ...it, isNew: w.baselined && !seen[String(it.id)] }));
        nextResults[w.id] = {
          loading: false,
          error: null,
          items: flagged,
          newCount: flagged.filter((f) => f.isNew).length,
        };
        baselinedOk[w.id] = true;
      } catch (e: any) {
        nextResults[w.id] = { loading: false, error: e?.message || "Sökningen misslyckades", items: [], newCount: 0 };
      }
    }

    // Record seen ids (prune old ones) and mark successfully-run watches baselined.
    const now = Date.now();
    const newSeen: Record<string, number> = {};
    for (const [id, ts] of Object.entries(seen)) if (now - ts < SEEN_TTL_MS) newSeen[id] = ts;
    for (const id of seenUpdates) newSeen[id] = now;
    const newWatches = cur.watches.map((w) => (baselinedOk[w.id] && !w.baselined ? { ...w, baselined: true } : w));
    const updated: TraderaStore = { watches: newWatches, seen: newSeen };

    setResults(nextResults);
    setStore(updated);
    setRefreshing(false);
    saveTradera(userId, updated).catch(() => {});
  }, [userId]);

  // Initial load, then an automatic refresh so opening the tab shows what's new.
  useEffect(() => {
    let cancelled = false;
    loadTradera(userId).then((s) => {
      if (cancelled) return;
      setStore(s);
      storeRef.current = s;
      setLoaded(true);
      if (!ranOnce.current && s.watches.length) {
        ranOnce.current = true;
        runAll();
      }
    });
    return () => { cancelled = true; };
  }, [userId, runAll]);

  const addWatch = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const q = query.trim();
      if (!q) return;
      const w: Watch = {
        id: uid(),
        query: q,
        minPrice: minPrice.trim() ? Math.round(Number(minPrice)) : null,
        maxPrice: maxPrice.trim() ? Math.round(Number(maxPrice)) : null,
        excludeWords: exclude.split(",").map((s) => s.trim()).filter(Boolean),
        categoryId: category.trim() ? Math.max(0, Math.round(Number(category))) : 0,
        baselined: false,
      };
      const updated: TraderaStore = { ...storeRef.current, watches: [...storeRef.current.watches, w] };
      setStore(updated);
      storeRef.current = updated;
      setQuery(""); setMinPrice(""); setMaxPrice(""); setExclude(""); setCategory("");
      await saveTradera(userId, updated).catch(() => {});
      // Run just the new watch (first run baselines it — nothing flagged "new").
      setResults((p) => ({ ...p, [w.id]: { loading: true, error: null, items: [], newCount: 0 } }));
      try {
        const resp = await searchTradera(w.query, w.categoryId, 0);
        const seen = { ...storeRef.current.seen };
        const now = Date.now();
        for (const it of resp.items) if (Number.isFinite(it.id)) seen[String(it.id)] = now;
        const items = resp.items.filter((it) => matches(w, it)).map((it) => ({ ...it, isNew: false }));
        setResults((p) => ({ ...p, [w.id]: { loading: false, error: null, items, newCount: 0 } }));
        const base: TraderaStore = {
          watches: storeRef.current.watches.map((x) => (x.id === w.id ? { ...x, baselined: true } : x)),
          seen,
        };
        setStore(base);
        storeRef.current = base;
        saveTradera(userId, base).catch(() => {});
      } catch (err: any) {
        setResults((p) => ({ ...p, [w.id]: { loading: false, error: err?.message || "Sökningen misslyckades", items: [], newCount: 0 } }));
      }
    },
    [query, minPrice, maxPrice, exclude, category, userId],
  );

  const removeWatch = useCallback(
    (id: string) => {
      const updated: TraderaStore = { ...storeRef.current, watches: storeRef.current.watches.filter((w) => w.id !== id) };
      setStore(updated);
      storeRef.current = updated;
      setResults((p) => { const n = { ...p }; delete n[id]; return n; });
      saveTradera(userId, updated).catch(() => {});
    },
    [userId],
  );

  const startEdit = useCallback((w: Watch) => {
    setEditingId(w.id);
    setEQuery(w.query);
    setEMin(w.minPrice != null ? String(w.minPrice) : "");
    setEMax(w.maxPrice != null ? String(w.maxPrice) : "");
    setEExclude(w.excludeWords.join(", "));
    setECategory(w.categoryId ? String(w.categoryId) : "");
  }, []);

  const cancelEdit = useCallback(() => setEditingId(null), []);

  const saveEdit = useCallback(
    async (e: React.FormEvent, id: string) => {
      e.preventDefault();
      const q = eQuery.trim();
      if (!q) return;
      const updated: TraderaStore = {
        ...storeRef.current,
        watches: storeRef.current.watches.map((w) =>
          w.id === id
            ? {
                ...w,
                query: q,
                minPrice: eMin.trim() ? Math.round(Number(eMin)) : null,
                maxPrice: eMax.trim() ? Math.round(Number(eMax)) : null,
                excludeWords: eExclude.split(",").map((s) => s.trim()).filter(Boolean),
                categoryId: eCategory.trim() ? Math.max(0, Math.round(Number(eCategory))) : 0,
              }
            : w,
        ),
      };
      setStore(updated);
      storeRef.current = updated;
      setEditingId(null);
      await saveTradera(userId, updated).catch(() => {});
      // Re-run so the edited filters/query take effect immediately.
      runAll();
    },
    [eQuery, eMin, eMax, eExclude, eCategory, userId, runAll],
  );

  const totalNew = Object.values(results).reduce((s, r) => s + (r?.newCount ?? 0), 0);

  return (
    <div className="tradera">
      <div className="tradera__header">
        <NavLink to={routeUrls.PROJEKT} className="tradera__back">← Projekt</NavLink>
        <h1 className="tradera__title">Tradera</h1>
        <div className="tradera__header-right">
          {store.watches.length > 0 && (
            <button className="tradera__refresh" onClick={runAll} disabled={refreshing}>
              {refreshing ? "Uppdaterar…" : "Uppdatera"}
            </button>
          )}
        </div>
      </div>

      <p className="tradera__intro">
        Bevaka sökningar på Tradera. När du öppnar sidan körs alla bevakningar och
        annonser du inte sett tidigare markeras <strong>NY</strong>.
        {totalNew > 0 && <> — <strong>{totalNew} nya</strong> just nu.</>}
      </p>

      <form className="tradera__addform" onSubmit={addWatch}>
        <input
          className="tradera__in tradera__in--q"
          placeholder="Sökord (sv + eng), t.ex. ”rörstrand servis / dinnerware”"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          required
        />
        <input className="tradera__in" type="number" min="0" placeholder="Min kr" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
        <input className="tradera__in" type="number" min="0" placeholder="Max kr" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
        <input className="tradera__in" placeholder="Uteslut ord (komma)" value={exclude} onChange={(e) => setExclude(e.target.value)} />
        <input className="tradera__in tradera__in--cat" type="number" min="0" placeholder="Kategori (0 = alla)" value={category} onChange={(e) => setCategory(e.target.value)} />
        <button className="tradera__add" type="submit">Lägg till bevakning</button>
      </form>

      {loaded && store.watches.length === 0 && (
        <p className="tradera__empty">Inga bevakningar än. Lägg till en ovan — första körningen visar bara vad som finns, sedan markeras nya annonser.</p>
      )}

      {store.watches.map((w) => {
        const r = results[w.id];
        const summary = filterSummary(w);
        return (
          <section className="tradera__watch" key={w.id}>
            <div className="tradera__watch-head">
              <div className="tradera__watch-headl">
                <h2 className="tradera__watch-title">
                  {w.query}
                  {r && r.newCount > 0 && <span className="tradera__watch-new">{r.newCount} nya</span>}
                </h2>
                {summary && <p className="tradera__watch-meta">{summary}</p>}
              </div>
              <div className="tradera__watch-actions">
                <button className="tradera__watch-edit" onClick={() => (editingId === w.id ? cancelEdit() : startEdit(w))}>
                  {editingId === w.id ? "Stäng" : "Ändra"}
                </button>
                <button className="tradera__watch-del" onClick={() => removeWatch(w.id)}>Ta bort</button>
              </div>
            </div>

            {editingId === w.id && (
              <form className="tradera__addform tradera__editform" onSubmit={(e) => saveEdit(e, w.id)}>
                <input className="tradera__in tradera__in--q" placeholder="Sökord" value={eQuery} onChange={(e) => setEQuery(e.target.value)} required />
                <input className="tradera__in" type="number" min="0" placeholder="Min kr" value={eMin} onChange={(e) => setEMin(e.target.value)} />
                <input className="tradera__in" type="number" min="0" placeholder="Max kr" value={eMax} onChange={(e) => setEMax(e.target.value)} />
                <input className="tradera__in" placeholder="Uteslut ord (komma)" value={eExclude} onChange={(e) => setEExclude(e.target.value)} />
                <input className="tradera__in tradera__in--cat" type="number" min="0" placeholder="Kategori (0 = alla)" value={eCategory} onChange={(e) => setECategory(e.target.value)} />
                <button className="tradera__add" type="submit">Spara</button>
                <button className="tradera__watch-del" type="button" onClick={cancelEdit}>Avbryt</button>
              </form>
            )}

            {editingId !== w.id && r?.loading && <p className="tradera__status">Söker…</p>}
            {editingId !== w.id && r?.error && <p className="tradera__status tradera__status--error">{r.error}</p>}
            {editingId !== w.id && r && !r.loading && !r.error && r.items.length === 0 && (
              <p className="tradera__status">Inga träffar inom filtren.</p>
            )}

            {editingId !== w.id && r && r.items.length > 0 && (
              <div className="tradera__results">
                {r.items.map((it) => (
                  <a key={it.id} className={`tradera__item${it.isNew ? " tradera__item--new" : ""}`} href={it.itemUrl} target="_blank" rel="noopener noreferrer">
                    {it.isNew && <span className="tradera__badge">NY</span>}
                    <div className="tradera__thumb">
                      {it.thumbnailLink ? <img src={it.thumbnailLink} alt="" loading="lazy" /> : <span className="tradera__thumb-ph" />}
                    </div>
                    <div className="tradera__item-body">
                      <span className="tradera__item-title">{it.shortDescription}</span>
                      <div className="tradera__item-prices">
                        <span className="tradera__price">Bud {fmtSek(it.maxBid)}{it.bidCount > 0 ? ` · ${it.bidCount} bud` : " · inga bud"}</span>
                        {it.buyItNowPrice != null && <span className="tradera__price tradera__price--bn">Köp nu {fmtSek(it.buyItNowPrice)}</span>}
                      </div>
                      <div className="tradera__item-meta">
                        <span>{it.sellerAlias}</span>
                        <span>{fmtEnd(it.endDate)}</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <p className="tradera__foot">Endast sökning och bevakning — inga bud läggs. Data via Traderas officiella API.</p>
    </div>
  );
}
